import { supabase } from './supabaseClient';

// --- Types ---
export interface User {
  username: string;
}

export interface SignupDetails {
  username: string;
  email: string;
  password: string;
}

export interface LoginDetails {
  email: string;
  password: string;
}

// --- Public API ---

/**
 * Signs up a new user using Supabase Auth.
 */
export const signup = async ({ username, email, password }: SignupDetails): Promise<{ success: boolean; message: string; }> => {
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: {
      data: {
        username: username,
      },
      // This is the URL the user will be redirected to after clicking the
      // confirmation link in their sign-up email.
      //
      // IMPORTANT: For this to work, you MUST add this exact URL to your
      // Supabase project's "Redirect URLs" allow-list under:
      // Authentication -> URL Configuration.
      //
      // Failure to add it to the allow-list will cause Supabase to ignore
      // this parameter and default to the Site URL or the referrer URL,
      // which is why confirmation links may point to the development environment.
      emailRedirectTo: 'https://btcgain.app/',
    },
  });

  if (error) {
    return { success: false, message: error.message };
  }
  
  // Supabase sends a confirmation email automatically.
  // We need to check if a new user was created but is awaiting confirmation.
  if (data.user && data.user.identities && data.user.identities.length === 0) {
      return { success: false, message: 'This email is already in use. Please try logging in.'}
  }

  return { success: true, message: 'Signup successful! Please check your email for a confirmation link.' };
};

/**
 * Logs in a user using Supabase Auth.
 */
export const login = async ({ email, password }: LoginDetails): Promise<{ success: boolean; message: string; user?: User }> => {
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password,
  });

  if (error) {
    return { success: false, message: error.message };
  }

  if (data.user) {
    const user: User = { username: data.user.user_metadata.username || data.user.email };
    return { success: true, message: 'Login successful!', user: user };
  }
  
  return { success: false, message: 'An unknown error occurred.' };
};

/**
 * Logs out the current user.
 */
export const logout = async (): Promise<void> => {
  await supabase.auth.signOut();
};

/**
 * Gets the currently logged-in user from the session.
 * NOTE: It's better to use onAuthStateChange listener in the main app component.
 */
export const getCurrentUser = async (): Promise<User | null> => {
  const { data: { session } } = await supabase.auth.getSession();
  if (session && session.user) {
    return { username: session.user.user_metadata.username || session.user.email };
  }
  return null;
};
